import React, { useState, useRef, useEffect } from "react";
import { Plus } from "lucide-react";
import BrandText from "./BrandText";

/**
 * CollapsibleSection — Editorial accordion row.
 *
 * Props:
 * - title:    "CARS & COFFEE"
 * - eyebrow:  "Collection 02" (small overline above title)
 * - meta:     "78 Photos" or "Riyadh · Saudi Arabia"  (optional)
 * - description: paragraph shown after expand
 * - defaultOpen: bool (default false)
 * - testid:   string
 * - variant:  "dark" (default) | "light" — flips text/border tokens for
 *             ivory-family pages (Events). Behavior and structure are
 *             identical across variants; only tonal tokens change.
 * - children: typically <MediaGallery items={...} />
 */
export default function CollapsibleSection({
    id,
    title,
    eyebrow,
    meta,
    description,
    defaultOpen = false,
    testid,
    variant = "dark",
    children,
}) {
    const isLight = variant === "light";
    const cls = {
        section: isLight
            ? "border-t border-[var(--crewz-text-dark)]/10 first:border-t-0"
            : "border-t border-white/10 first:border-t-0",
        eyebrow: isLight
            ? "overline mb-3 text-[var(--crewz-text-dark)]/55"
            : "overline mb-3 text-zinc-500",
        title: isLight
            ? "font-display text-4xl md:text-6xl lg:text-7xl text-[var(--crewz-text-dark)] leading-[0.95] group-hover:text-[var(--crewz-gold)] transition-colors duration-300"
            : "font-display text-4xl md:text-6xl lg:text-7xl text-[var(--crewz-text)] leading-[0.95] group-hover:text-[var(--crewz-gold)] transition-colors duration-300",
        meta: isLight
            ? "font-body text-xs md:text-sm tracking-[0.22em] uppercase font-medium text-[var(--crewz-text-dark)]/55 mt-5"
            : "font-body text-xs md:text-sm tracking-[0.22em] uppercase font-medium text-[var(--crewz-text)]/55 mt-5",
        plusBorderBase: isLight
            ? "shrink-0 w-12 h-12 md:w-14 md:h-14 border border-[var(--crewz-text-dark)]/15 group-hover:border-[var(--crewz-gold)] flex items-center justify-center transition-all duration-500"
            : "shrink-0 w-12 h-12 md:w-14 md:h-14 border border-white/15 group-hover:border-[var(--crewz-gold)] flex items-center justify-center transition-all duration-500",
        plusIconIdle: isLight
            ? "text-[var(--crewz-text-dark)]/60 group-hover:text-[var(--crewz-gold)]"
            : "text-zinc-300 group-hover:text-[var(--crewz-gold)]",
        description: isLight
            ? "font-body text-base md:text-lg text-[var(--crewz-text-dark)]/80 max-w-3xl font-light leading-relaxed mb-12"
            : "font-body text-base md:text-lg text-[var(--crewz-text)]/80 max-w-3xl font-light leading-relaxed mb-12",
    };
    const [open, setOpen] = useState(defaultOpen);
    const [maxHeight, setMaxHeight] = useState(defaultOpen ? "none" : 0);
    const innerRef = useRef(null);

    useEffect(() => {
        if (!innerRef.current) return;
        if (open) {
            // Expand: set explicit height, then to none after transition for natural layout
            const h = innerRef.current.scrollHeight;
            setMaxHeight(h);
            const t = setTimeout(() => setMaxHeight("none"), 600);
            return () => clearTimeout(t);
        } else {
            // Collapse: explicit height first, then to 0 next frame
            if (innerRef.current.scrollHeight) {
                setMaxHeight(innerRef.current.scrollHeight);
                requestAnimationFrame(() => setMaxHeight(0));
            } else {
                setMaxHeight(0);
            }
        }
    }, [open]);

    return (
        <section
            id={id}
            className={`${cls.section} scroll-mt-24 md:scroll-mt-28`}
            data-testid={testid}
        >
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="group w-full text-left py-10 md:py-14 flex items-start gap-6 transition-colors duration-300"
                aria-expanded={open}
                data-testid={`${testid}-toggle`}
            >
                <div className="flex-1 min-w-0">
                    {eyebrow && (
                        <p className={cls.eyebrow}>{eyebrow}</p>
                    )}
                    <h2 className={cls.title}>
                        <BrandText>{title}</BrandText>
                    </h2>
                    {meta && (
                        <p className={cls.meta}>
                            {meta}
                        </p>
                    )}
                </div>
                <div
                    className={`${cls.plusBorderBase} ${
                        open ? "rotate-45 border-[var(--crewz-gold)]" : ""
                    }`}
                    aria-hidden
                >
                    <Plus
                        size={22}
                        className={`transition-colors duration-300 ${
                            open
                                ? "text-[var(--crewz-gold)]"
                                : cls.plusIconIdle
                        }`}
                    />
                </div>
            </button>

            <div
                style={{
                    maxHeight: maxHeight === "none" ? "none" : `${maxHeight}px`,
                    overflow: "hidden",
                    transition: "max-height 500ms cubic-bezier(0.4, 0, 0.2, 1)",
                }}
                aria-hidden={!open}
            >
                <div ref={innerRef} className="pb-16 md:pb-20">
                    {description && (
                        <p className={cls.description}>
                            <BrandText>{description}</BrandText>
                        </p>
                    )}
                    {children}
                </div>
            </div>
        </section>
    );
}
